import { generateBetItemId } from '../utils/ticketHelpers';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SportMatchesPage from './SportMatchesPage';
import { BetItem } from './BettingSlipDrawer';
import { getTournaments, getCatalogHome } from '../api/api';
import { SportItem, EventSnapshot, Market, Outcome } from '../api/types';
import { SPORT_EMOJI, ALLOWED_SPORTS } from './widgets/DesktopLeftSidebar';
import { useTranslation } from '../i18n';

const timeFilters = [
  { id: 'alle', label: 'Alle', svgIcon: (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="21" r="13.5" fill="#ffffff" stroke="#D81E29" strokeWidth="4" />
      <path d="M19.5 4 A16.5 16.5 0 0 1 32.5 10.5" stroke="#D81E29" strokeWidth="4" strokeLinecap="round" />
      <path d="M17.5 1.5 L22.7 4.3 L17.8 7.4 Z" fill="#D81E29" />
      <text x="20" y="27.5" fontFamily="Arial, Helvetica, sans-serif" fontSize="17" fontWeight="800" fill="#D81E29" textAnchor="middle">∞</text>
    </svg>
  ) },
  { id: 'heute', label: 'Heute', image: '/images/haute.png' },
  { id: '3std', label: '3 Std.', image: '/images/3-hours.png' },
  { id: '48std', label: '48 Std.', image: '/images/48-hours.png' },
];



interface SportEntry {
  id: string;
  label: string;
  emoji: string;
  count: number;
  sportId: string;
}

interface SportPageProps {
  isLogged: boolean;
  onLoginClick: () => void;
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  initialCompetitionName?: string | null;
  onClearInitialCompetition?: () => void;
  initialSportName?: string | null;
  onClearInitialSport?: () => void;
}

const SportPage: React.FC<SportPageProps> = ({
  isLogged, onLoginClick, bets, onAddBet, onRemoveBet,
  initialCompetitionName, onClearInitialCompetition,
  initialSportName, onClearInitialSport
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sportName, categoryName, tournamentName } = useParams<{
    sportName?: string;
    categoryName?: string;
    tournamentName?: string;
  }>();

  const [activeTime, setActiveTime] = useState('alle');
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [allEvents, setAllEvents] = useState<EventSnapshot[]>([]);
  const [fetchingEvents, setFetchingEvents] = useState(false);
  const [sports, setSports] = useState<SportItem[]>([]);
  const [loadingSports, setLoadingSports] = useState(true);

  useEffect(() => {
    let params: any = {};
    if (activeTime === 'alle') {
      params.starts_after_hours = 0;
      params.starts_before_hours = 2160;
    } else if (activeTime === 'heute') {
      params.starts_after_hours = 0;
      params.starts_before_hours = 24;
    } else if (activeTime === '3std') {
      params.starts_after_hours = 0;
      params.starts_before_hours = 3;
    } else if (activeTime === '48std') {
      params.starts_after_hours = 0;
      params.starts_before_hours = 48;
    }

    setLoadingSports(true);
    getTournaments(params)
      .then((r) => {
        const aggr: Record<string, SportItem> = {};
        (r.items || []).forEach((t: any) => {
          if (!aggr[t.sport_name]) {
            aggr[t.sport_name] = {
              sport_id: t.sport_id,
              sport_name: t.sport_name,
              event_count: 0
            };
          }
          aggr[t.sport_name].event_count += t.event_count;
        });
        setSports(Object.values(aggr));
      })
      .catch(() => {})
      .finally(() => setLoadingSports(false));
  }, [activeTime]);

  const toEntry = (s: SportItem): SportEntry => ({
    id: s.sport_name,
    sportId: s.sport_id,
    label: ALLOWED_SPORTS[s.sport_name] || s.sport_name,
    emoji: SPORT_EMOJI[s.sport_name] ?? '🏅',
    count: s.event_count,
  });

  const selectedSport = sportName && sports.length > 0
    ? sports.map(s => toEntry(s)).find(s => s.id === sportName) || null
    : null;

  const TOP_SPORTS_ORDER_BY_ID: Record<string, number> = {
    'sr:sport:1': 1,
    'sr:sport:2': 2,
    'sr:sport:5': 3,
    'sr:sport:4': 4,
    'sr:sport:23': 5,
    'sr:sport:6': 6
  };

  const topSports: SportEntry[] = [...sports]
    .filter(s => s.sport_id in TOP_SPORTS_ORDER_BY_ID)
    .sort((a, b) => TOP_SPORTS_ORDER_BY_ID[a.sport_id] - TOP_SPORTS_ORDER_BY_ID[b.sport_id])
    .map(s => toEntry(s));

  const azSports: SportEntry[] = [...sports]
    .filter(s => !(s.sport_id in TOP_SPORTS_ORDER_BY_ID))
    .sort((a, b) => {
      const nameA = ALLOWED_SPORTS[a.sport_name] || a.sport_name;
      const nameB = ALLOWED_SPORTS[b.sport_name] || b.sport_name;
      return nameA.localeCompare(nameB, 'de');
    })
    .map(s => toEntry(s));

  const filtered = (list: SportEntry[]) =>
    list.filter((s) => s.label.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    if ((searchFocused || search.length > 0) && allEvents.length === 0 && !fetchingEvents) {
      setFetchingEvents(true);
      getCatalogHome()
        .then((r) => setAllEvents([...r.live, ...r.pre]))
        .catch(() => {})
        .finally(() => setFetchingEvents(false));
    }
  }, [searchFocused, search, allEvents.length, fetchingEvents]);

  const filteredMatches = useMemo(() => {
    if (search.length < 2) return [];
    const q = search.toLowerCase();
    return allEvents.filter(ev =>
      ev.event.home.name.toLowerCase().includes(q) ||
      ev.event.away.name.toLowerCase().includes(q) ||
      ev.event.tournament_name.toLowerCase().includes(q) ||
      (ev.event.category_name && ev.event.category_name.toLowerCase().includes(q)) ||
      (ev.event.sport_name && ev.event.sport_name.toLowerCase().includes(q))
    );
  }, [allEvents, search]);

  const searchResultsGrouped = useMemo(() => {
    const grouped: Record<string, Record<string, EventSnapshot[]>> = {};
    filteredMatches.forEach((ev) => {
      const s = ev.event.sport_name || 'Other';
      const t = ev.event.tournament_name || 'Other';
      if (!grouped[s]) grouped[s] = {};
      if (!grouped[s][t]) grouped[s][t] = [];
      grouped[s][t].push(ev);
    });
    return grouped;
  }, [filteredMatches]);

  const formatOdds = (val: string | number): string => {
    const n = parseFloat(String(val));
    if (isNaN(n) || n <= 1) return '-';
    return n.toFixed(2).replace('.', ',');
  };

  const find1x2 = (markets: Market[] | undefined) => {
    const m = markets?.find((mk) => mk.market_id === '1' && mk.status !== -1);
    if (!m) return null;
    const home = m.outcomes.find((o: Outcome) => o.outcome_id === '1');
    const draw = m.outcomes.find((o: Outcome) => o.outcome_id === '2' || o.name.toLowerCase() === 'draw' || o.name.toLowerCase() === 'x');
    const away = m.outcomes.find((o: Outcome) => o.outcome_id === '3');
    if (home && draw && away) return { home, draw, away, market: m };
    return null;
  };

  const isSelected = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) =>
    bets.some((b) => b.id === generateBetItemId(eventId, marketId, outcomeId, specifiers));

  useEffect(() => {
    if (initialCompetitionName && sports.length > 0 && !selectedSport) {
      const soccerSport = sports.find(s => s.sport_id === 'sr:sport:1');
      if (soccerSport) {
        navigate(`/sport/Soccer/${encodeURIComponent(initialCompetitionName)}`, { replace: true });
        if (onClearInitialCompetition) {
          onClearInitialCompetition();
        }
      }
    }
  }, [initialCompetitionName, sports, selectedSport, onClearInitialCompetition, navigate]);

  useEffect(() => {
    if (initialSportName && sports.length > 0 && !selectedSport) {
      const matchedSport = sports.find(s => s.sport_name === initialSportName);
      if (matchedSport) {
        navigate(`/sport/${encodeURIComponent(matchedSport.sport_name)}`, { replace: true });
        if (onClearInitialSport) {
          onClearInitialSport();
        }
      }
    }
  }, [initialSportName, sports, selectedSport, onClearInitialSport, navigate]);

  if (selectedSport) {
    return (
      <SportMatchesPage
        sportLabel={selectedSport.label}
        sportEmoji={selectedSport.emoji}
        sportId={selectedSport.sportId}
        timeFilter={activeTime}
        onBack={() => navigate('/sport')}
        isLogged={isLogged}
        onLoginClick={onLoginClick}
        bets={bets}
        onAddBet={onAddBet}
        onRemoveBet={onRemoveBet}
        initialTournamentName={initialCompetitionName}
        onClearInitialCompetition={onClearInitialCompetition}
        initialCategory={categoryName}
        initialTournament={tournamentName}
      />
    );
  }

  if (loadingSports) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Red Header with brand gradient */}
      <div className="px-4 pt-4 pb-6" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-white text-2xl font-black italic tracking-tighter select-none">sport</h1>
          {!isLogged && (
            <button
              onClick={onLoginClick}
              className="text-white font-bold px-4 py-1.5 rounded-full text-[13px] border border-white/30 hover:border-white transition uppercase tracking-wide bg-black/10"
            >
              Anmelden
            </button>
          )}
        </div>
        {/* Search */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🔍</span>
          <input
            type="text"
            value={search}
            onFocus={() => setSearchFocused(true)}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Team, Event oder Wettbewerb"
            className="w-full bg-white rounded-full pl-10 pr-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 font-bold"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Time Filter Tabs */}
      <div className="bg-[#f5f6f6] border-b border-gray-200 px-4 py-3 flex gap-4 overflow-x-auto w-full">
        {timeFilters.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveTime(f.id)}
            className={`flex flex-col items-center gap-1 w-1/4 pb-1 border-b-2 transition ${
              activeTime === f.id ? 'border-red-600' : 'border-transparent'
            }`}
          >
            <div className="relative w-10 h-10 flex items-center justify-center">
              {f.image ? (
                <img src={f.image} alt={f.label} className="w-10 h-10 object-contain rounded-[4px]" />
              ) : f.svgIcon}
            </div>
            <span className={`text-xs font-medium whitespace-nowrap ${
              activeTime === f.id ? 'text-gray-900' : 'text-gray-500'
            }`}>{f.label}</span>
          </button>
        ))}
      </div>

      {/* Sport Lists / Search Results */}
      <div className="px-4 pt-4">
        {search.length >= 2 ? (
          /* SEARCH RESULTS VIEW */
          <div className="flex flex-col gap-6">
            {Object.keys(searchResultsGrouped).length > 0 ? (
              Object.entries(searchResultsGrouped).map(([sport, tournaments]) => (
                <div key={sport} className="mb-2">
                  <h2 className="text-sm font-bold text-gray-800 uppercase flex items-center gap-2 mb-3">
                    {SPORT_EMOJI[sport] || '🏅'} {sport}
                  </h2>
                  <div className="flex flex-col gap-4">
                    {Object.entries(tournaments).map(([tournament, events]) => (
                      <div key={tournament} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-100 px-3 py-2">
                          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">{tournament}</h3>
                        </div>
                        {events.map((ev, eIdx) => {
                          const odds3way = find1x2(ev.markets);
                          return (
                            <div key={ev.event_id} className={`p-3 ${eIdx !== events.length - 1 ? 'border-b border-gray-100' : ''}`}>
                              <div className="flex flex-col gap-1.5 mb-2">
                                <div className="flex items-center gap-2 truncate">
                                  <img src={ev.event.home.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                  <span className="text-sm font-bold text-gray-900 leading-tight truncate">{ev.event.home.name}</span>
                                </div>
                                <div className="flex items-center gap-2 truncate">
                                  <img src={ev.event.away.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                  <span className="text-sm font-bold text-gray-900 leading-tight truncate">{ev.event.away.name}</span>
                                </div>
                              </div>
                              {odds3way && (
                                <div className="flex items-center gap-2 mt-2">
                                  {[
                                    { label: '1', outcome: odds3way.home, tipp: '1' },
                                    { label: 'X', outcome: odds3way.draw, tipp: 'X' },
                                    { label: '2', outcome: odds3way.away, tipp: '2' },
                                  ].map(({ label, outcome, tipp }) => {
                                    const sel = isSelected(ev.event_id, odds3way.market.market_id, outcome.outcome_id, odds3way.market.specifiers);
                                    return (
                                      <button
                                        key={label}
                                        onClick={(e) => { 
                                          e.stopPropagation(); 
                                          onAddBet({
                                            id: generateBetItemId(ev.event_id, odds3way.market.market_id, outcome.outcome_id, odds3way.market.specifiers),
                                            matchId: ev.event_id,
                                            matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
                                            betType: tipp === '1' ? 'home' : tipp === 'X' ? 'draw' : 'away',
                                            tippLabel: label,
                                            odds: formatOdds(outcome.odds),
                                            eventId: ev.event_id,
                                            marketId: odds3way.market.market_id,
                                            outcomeId: outcome.outcome_id,
                                          }); 
                                        }}
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-colors flex items-center justify-center gap-1 ${
                                          sel
                                            ? 'bg-gray-900 text-white border-gray-900'
                                            : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50'
                                        }`}
                                      >
                                        <span className="text-gray-400 font-normal">{label}</span>
                                        <span>{formatOdds(outcome.odds)}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500 font-medium">Keine Spiele gefunden für "{search}"</p>
              </div>
            )}
          </div>
        ) : (
          /* STANDARD SPORTS LIST */
          <>
            {filtered(topSports).length > 0 && (
              <>
                <h2 className="text-base font-bold text-gray-900 mb-2">Top</h2>
                <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-5">
                  {filtered(topSports).map((sport, idx, arr) => (
                    <button
                      key={sport.id}
                      onClick={() => navigate(`/sport/${encodeURIComponent(sport.id)}`)}
                      className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-gray-50 transition text-left ${
                        idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      <span className="text-2xl w-8 text-center">{sport.emoji}</span>
                      <span className="flex-1 text-sm font-medium text-gray-800">{sport.label}</span>
                      <span className="text-sm text-gray-500">{sport.count}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {filtered(azSports).length > 0 && (
              <>
                <h2 className="text-base font-bold text-gray-900 mb-2">A-Z</h2>
                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                  {filtered(azSports).map((sport, idx, arr) => (
                    <button
                      key={sport.id}
                      onClick={() => navigate(`/sport/${encodeURIComponent(sport.id)}`)}
                      className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-gray-50 transition text-left ${
                        idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      <span className="text-2xl w-8 text-center">{sport.emoji}</span>
                      <span className="flex-1 text-sm font-medium text-gray-800">{sport.label}</span>
                      <span className="text-sm text-gray-500">{sport.count}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {filtered(topSports).length === 0 && filtered(azSports).length === 0 && (
              <p className="text-center text-gray-500 text-sm mt-10">Keine Ergebnisse gefunden.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SportPage;
